// Create new file: src/components/NicuPerformanceChart.js

import { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import { format, eachDayOfInterval, startOfMonth, endOfMonth } from 'date-fns';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const API_BASE_URL = "https://api.muktihospital.com/api";

const NicuPerformanceChart = () => {
    const [chartData, setChartData] = useState({ series: [], options: {} });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchChartData = async () => {
            setLoading(true);
            const now = new Date();
            const year = now.getFullYear();
            const month = now.getMonth() + 1;
            const monthPadded = String(month).padStart(2, '0');
            const firstDay = startOfMonth(now);
            const lastDay = endOfMonth(now);
            const daysOfMonth = eachDayOfInterval({ start: firstDay, end: lastDay });

            const labels = daysOfMonth.map(day => format(day, 'd'));
            const dailyAdmissions = Array(daysOfMonth.length).fill(0);
            const dailyBillAmount = Array(daysOfMonth.length).fill(0);

            try {
                const api = axios.create({ baseURL: API_BASE_URL, headers: { "x-api-key": API_KEY } });

                const [admissionsRes, billsRes] = await Promise.all([
                    api.get(`/nicu/admission`),
                    api.get(`/nicu/nicu-bills/bills/monthly?year=${year}&month=${monthPadded}`)
                ]);
                
                const monthlyAdmissions = admissionsRes.data?.data?.admissions || [];
                const monthlyBills = billsRes.data?.bills || [];
                
                daysOfMonth.forEach((day, index) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    
                    const admissionsForDay = monthlyAdmissions.filter(admission => 
                        admission.admissionDate && admission.admissionDate.startsWith(dateStr)
                    );
                    dailyAdmissions[index] = admissionsForDay.length;

                    const billsForDay = monthlyBills.filter(bill => 
                        bill.billingDate && bill.billingDate.startsWith(dateStr)
                    );
                    dailyBillAmount[index] = billsForDay.reduce((sum, bill) => sum + (bill.netAmount || 0), 0);
                });
                
                setChartData({
                    series: [
                        { name: 'Admissions', type: 'column', data: dailyAdmissions },
                        { name: 'Bill Amount', type: 'line', data: dailyBillAmount }
                    ],
                    options: {
                        chart: { height: 400, type: 'line', toolbar: { show: false } },
                        stroke: { width: [0, 4], curve: 'smooth' },
                        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
                        dataLabels: { enabled: false },
                        labels: labels,
                        yaxis: [
                            {
                                title: { text: 'Number of Admissions' },
                                labels: { formatter: (val) => Math.round(val) }
                            },
                            {
                                opposite: true,
                                title: { text: 'Total Bill Amount (৳)' },
                                labels: { formatter: (val) => `৳${(val / 1000).toFixed(0)}k` }
                            }
                        ],
                        colors: ["#6AC75A", "#465DFF"],
                        tooltip: { 
                            y: { 
                                formatter: (val, { seriesIndex }) => {
                                    if (seriesIndex === 0) {
                                        return `${Math.round(val)} admissions`;
                                    } else { 
                                        return `৳${Math.round(val).toLocaleString()}`;
                                    }
                                }
                            } 
                        }
                    },
                });

            } catch (error) {
                console.error("Error fetching chart data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, []);

    if (loading) {
        return <Skeleton height={400} />;
    }

    return (
        <div>
            <ReactApexChart
                options={chartData.options}
                series={chartData.series}
                type="line"
                height={400}
            />
        </div>
    );
};

export default NicuPerformanceChart;